// Nasłuch swobodnej wypowiedzi (rozmowa z botem): zwykłe rozpoznawanie mowy BEZ zdania wzorcowego i bez oceny wymowy.
// Azure (przez ten sam token i SDK co lekcje) albo, gdy go brak, rozpoznawanie przeglądarki. Cisza to nie błąd: zwraca pusty tekst.
import { errorFromRecognition, getRecognitionCtor } from './assessors.ts';
import { defaultSdkLoader, openBrowserMic, plainDetailsFrom, runAzurePlain } from './azure.ts';
import type { MicOpener, PlainRunArgs, SdkLike, SdkLoader, TokenProvider } from './azure.ts';
import { AssessError } from './types.ts';
import type { AssessHooks } from './types.ts';

export interface Heard {
  text: string;
  /** Pewność rozpoznania 0–1 (tylko Azure). */
  confidence?: number;
}

/** Język nasłuchu: rozmowa z botem po angielsku, pytania w zadaniach zwykle po polsku. */
export type ListenLang = 'en-US' | 'pl-PL';

export interface ListenHooks extends AssessHooks {
  /** Język rozpoznawania (domyślnie en-US). */
  lang?: ListenLang;
}

export interface Listener {
  name: 'azure' | 'browser';
  /** Nasłuchuje jednej wypowiedzi. `onReady` = mikrofon gotowy, można mówić. Pusty tekst = nic nie usłyszano. */
  listen(hooks?: ListenHooks): Promise<Heard>;
}

export type AzureListenerOptions = Pick<PlainRunArgs, 'timeoutMs' | 'startTimeoutMs' | 'segmentationSilenceMs'>;

export function createAzureListener(tokens: TokenProvider, loader: SdkLoader = defaultSdkLoader, openMic: MicOpener = openBrowserMic, options: AzureListenerOptions = {}): Listener {
  let sdkPromise: Promise<SdkLike> | null = null;
  return {
    name: 'azure',
    async listen(hooks) {
      const { token, region } = await tokens.get();
      sdkPromise ??= loader();
      let sdk: SdkLike;
      try {
        sdk = await sdkPromise;
      } catch (e) {
        sdkPromise = null;
        throw new AssessError('sdk', e instanceof Error ? e.message : String(e));
      }
      const mic = await openMic(sdk);
      try {
        let raw: string | undefined;
        const text = await runAzurePlain({
          sdk,
          token,
          region,
          audio: mic.newAudio(),
          onRaw: (json) => void (raw = json),
          ...(hooks?.onReady ? { onStart: hooks.onReady } : {}),
          ...(hooks?.lang ? { lang: hooks.lang } : {}),
          ...options,
        });
        const d = plainDetailsFrom(raw, text);
        return { text: d.text.trim(), ...(d.confidence !== undefined ? { confidence: d.confidence } : {}) };
      } finally {
        mic.release();
      }
    },
  };
}

/** Rozpoznawanie przeglądarki (Web Speech API) jako zapas. `null`, gdy przeglądarka go nie ma. */
export function createBrowserListener(scope: object = globalThis): Listener | null {
  const Ctor = getRecognitionCtor(scope);
  if (!Ctor) return null;
  return {
    name: 'browser',
    listen(hooks) {
      return new Promise<Heard>((resolve, reject) => {
        const rec = new Ctor();
        rec.lang = hooks?.lang ?? 'en-US';
        rec.interimResults = false;
        rec.maxAlternatives = 1;
        rec.continuous = false;
        let settled = false;
        let audioStarted = false;
        rec.onaudiostart = () => {
          audioStarted = true;
          hooks?.onReady?.();
        };
        rec.onresult = (e) => {
          settled = true;
          resolve({ text: (e.results[0]?.[0]?.transcript ?? '').trim() });
        };
        rec.onerror = (e) => {
          settled = true;
          if (e.error === 'no-speech' || e.error === 'aborted') return resolve({ text: '' });
          reject(errorFromRecognition(e.error));
        };
        rec.onend = () => {
          if (settled) return;
          settled = true;
          if (audioStarted) resolve({ text: '' });
          else reject(new AssessError('no-mic', 'przeglądarka zakończyła nasłuch bez uruchomienia mikrofonu (brak zdarzenia audiostart)'));
        };
        try {
          rec.start();
        } catch (e) {
          reject(new AssessError('unsupported', e instanceof Error ? e.message : String(e)));
        }
      });
    },
  };
}
